import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import Datepicker, { DateType } from "react-tailwindcss-datepicker";
import {
  myTaskCheckListCommentsListName,
  myTaskCheckListConnectionWithTasksListName,
  myTaskCheckListDirectionsListName,
  myTaskCheckListResultsListName,
  myTaskCheckListsListName,
  myTaskCheckListTasksListName,
  myTaskShiftListName,
} from "src/api/config/meetings.config";
import MyTaskCheckListTable from "src/components/tables/meetingTables/meetingsCheckListTable/MyTaskCheckListTable";
import { DownloadWithHoverIcon } from "src/components/ui-icons/upload/DownloadIcon";
import { TGDisclosure } from "src/components/ui/TGDisclosure";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { addItemRequest, getItemsRequest } from "src/modules/spItems/api";
import {
  modifiedItemsState,
  selectItems,
  selectJustAddedItem,
  selectJustUpdatedItem,
} from "src/modules/spItems/selectors";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import ExportToExcel, { createExcelBlob } from "./../../../../modules/exelExport/exelExport";
import TGButton from "src/components/ui/TGButton";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import EyeIcon from "src/components/ui-icons/reports/EyeIcon";
import CirclePlus from "./icon/CirclePlus";
import { Option, TGSelectPickerByList } from "src/components/ui/TGSelect";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { SearchUI } from "src/components/ui/Search";
import CheckListAdditionalMeetingFields from "./CheckListAdditionalMeetingFields";
import { AdditionalFieldsProvider } from "../meetingsPage/providers/additionalFieldsProvider";
import { frequencyCheckList } from "../meetingsPage/components/CreateMeetingButton";
import { useSearchParams } from "react-router-dom";
import {
  completeChecklistShift,
  fetchChecklistTasks,
  getStartEndDateByPeriod,
  resetChecklistPeriodDate,
  resetChecklistTasks,
  returnChecklistShiftToWork,
  selectChecklistPeriodDate,
  selectChecklistShiftItems,
  selectChecklistShiftMutationStatus,
  selectChecklistShiftStatus,
  selectChecklistTasks,
  selectChecklistTasksStatus,
  setChecklistPeriodDate,
  setChecklistSelectedTaskId,
  TaskItem,
  updateChecklistTaskLocally,
  getChecklistEarliestEditableDate,
  getChecklistShiftDayDate,
} from "src/modules/checklistTasks";
import { getTaskByID } from "src/modules/checklistTasks/api";
import { myTaskActionsListName } from "src/api/config/myTasksActions.config";

import { addItemSuccess, getItems } from "src/modules/spItems/actions";
import Loading from "src/components/Loading";
import { LookUpRecord } from "src/types/main";
import MyTaskCheckListMyTeamTable from "src/components/tables/meetingTables/meetingsCheckListTable/MyTaskCheckListMyTeamTable.mobile";
import { emailAPI } from "src/utils/api.utils";
import { strapiUrl } from "src/modules/users/constants";
import { twMerge } from "tailwind-merge";

type ColumnKey = (typeof columns)[number]["key"]; // Извлекаем тип ключей из columns

type ExportDataType = {
  columns: typeof columns;
  data: Array<Record<ColumnKey, any>>; // Каждый объект в data должен содержать ключи из ColumnKey
  highlightPinkRowIndexes?: number[];
};

const columns = [
  { header: "Направление", key: "Direction", width: 20 },
  { header: "Периодичность", key: "Period", width: 40 },
  { header: "Название задачи", key: "Title", width: 25 },
  { header: "Критерий выполнения", key: "CompletionCriteria", width: 70 },
  { header: "Комментарии", key: "Comments", width: 70 },
  { header: "Ссылки на действия", key: "ActionLinks", width: 35 },
  { header: "Отметка о выполнении", key: "isDone", width: 15 },
  { header: "Отметка об отклонении", key: "hasDeviation", width: 25 },
] as const;

const exportTempData: ExportDataType = {
  columns: columns,
  data: [],
};

const getLookupId = (lookup: any) => {
  if (!lookup) {
    return null;
  }

  if (lookup.get_lookupId) {
    return Number(lookup.get_lookupId());
  }

  return Number(lookup.ID || lookup.id || lookup);
};

const normalizeEmail = (email: any) =>
  String(email || "")
    .toLowerCase()
    .trim();

const getItemAuthorEmail = (item: any) =>
  normalizeEmail(
    item.Author?.get_email?.() ||
      item.Author?.Email ||
      item.Author?.email ||
      item.Author?.User?.Email ||
      item.Author?.User?.email ||
      item.AuthorEmail,
  );

const getTaskVisiblePeriod = (task: TaskItem, periodDate: DateType | undefined) =>
  getStartEndDateByPeriod(task.Period, periodDate || new Date());

const isDateInVisiblePeriod = (
  date: DateType | string | null | undefined,
  visiblePeriod: { start?: Date | string | null; end?: Date | string | null },
) => {
  if (!visiblePeriod.start || !visiblePeriod.end) {
    return true;
  }

  const targetDate = moment(date);

  if (!targetDate.isValid()) {
    return true;
  }

  return targetDate.isBetween(moment(visiblePeriod.start), moment(visiblePeriod.end), undefined, "[]");
};

const buildCommentsByTaskId = (
  comments: any[],
  tasks: TaskItem[],
  authorEmail: string,
  periodDate: DateType | undefined,
) => {
  const tasksByConnectionId = new Map<number, TaskItem>();
  tasks.forEach((task) => {
    const connectionId = Number(task.ConnectionID);
    if (connectionId) {
      tasksByConnectionId.set(connectionId, task);
    }
  });

  return comments.reduce<Record<string, string[]>>((acc, comment: any) => {
    const taskId = getLookupId(comment.Task);
    const textComment = String(comment.TextComment || "");
    const task = taskId ? tasksByConnectionId.get(Number(taskId)) : null;

    if (!taskId || !task || !textComment.trim()) {
      return acc;
    }

    if (getItemAuthorEmail(comment) !== authorEmail) {
      return acc;
    }

    if (!isDateInVisiblePeriod(comment.CreateCommentDate, getTaskVisiblePeriod(task, periodDate))) {
      return acc;
    }

    const key = String(taskId);
    acc[key] = [...(acc[key] || []), textComment];
    return acc;
  }, {});
};

const fetchActionsByTasks = async (
  tasks: TaskItem[],
  authorEmail: string,
  periodDate: DateType | undefined,
  signal?: AbortSignal,
) => {
  const tasksWithConnection = tasks.filter((task) => Boolean(task.ConnectionID));

  if (!tasksWithConnection.length) {
    return {};
  }

  const response = await getItemsRequest({
    listName: myTaskActionsListName,
    filters: {
      ImportantHashTag: tasksWithConnection.map((task) => `#чек-лист_${task.ConnectionID}*`),
      AuthorEmail: authorEmail,
    },
    signal,
  });

  return tasksWithConnection.reduce<Record<string, any[]>>((result, task) => {
    const connectionId = String(task.ConnectionID);
    const hashTag = `#чек-лист_${connectionId}`;
    const visiblePeriod = getTaskVisiblePeriod(task, periodDate);

    result[connectionId] = response.items.filter(
      (action) =>
        String(action.ImportantHashTag || "").trim() === hashTag &&
        normalizeEmail(action.AuthorEmail) === authorEmail &&
        isDateInVisiblePeriod(action.CreateActionDate, visiblePeriod),
    );
    return result;
  }, {});
};

function CheckListsMain({
  email,
  jobTitleStr,
  page = "main",
  minDate = getChecklistEarliestEditableDate(),
}: {
  email: string;
  jobTitleStr: string;
  page?: "main" | "myTeam";
  minDate?: Date;
}) {
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const users = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();
  const periodCheckListDate = useAppSelector(selectChecklistPeriodDate);

  const tasks = useAppSelector(selectChecklistTasks);
  const shiftItems = useAppSelector(selectChecklistShiftItems);
  const status = useAppSelector(selectChecklistTasksStatus);
  const shiftStatus = useAppSelector(selectChecklistShiftStatus);
  const shiftMutationStatus = useAppSelector(selectChecklistShiftMutationStatus);
  const [shiftComment, setShiftComment] = useState("");
  const [isCompletingShift, setIsCompletingShift] = useState(false);
  const [openReturnShiftModal, setOpenReturnShiftModal] = useState(false);
  const [actionsByConnectionId, setActionsByConnectionId] = useState<Record<string, any[]>>({});
  const [commentsByTaskId, setCommentsByTaskId] = useState<Record<string, string[]>>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const taskCardParam = searchParams.get("taskCard");
  const taskCardId = taskCardParam ? Number(taskCardParam) : null;
  const shiftDate = useMemo(
    () => (periodCheckListDate?.startDate ? moment(periodCheckListDate.startDate).format("YYYY-MM-DD") : ""),
    [periodCheckListDate?.startDate],
  );
  const completedShift = useMemo(
    () =>
      shiftItems.find(
        (shift) =>
          shift.Finished && shift.Date === shiftDate && shift.ResponsibleEmail.toLowerCase() === email.toLowerCase(),
      ),
    [shiftItems, shiftDate, email],
  );
  const isShiftCompleted = Boolean(completedShift);
  const isShiftActionLoading = isCompletingShift || shiftMutationStatus === "LOADING";
  const isShiftBlockingLoading = shiftStatus === "LOADING" || isShiftActionLoading;
  const loadingItems = status === "LOADING" || isShiftBlockingLoading;
  const updateConnectionStatus = useAppSelector((state) =>
    selectJustUpdatedItem(state, myTaskCheckListConnectionWithTasksListName),
  );
  const addTaskStatus = useAppSelector((state) => selectJustAddedItem(state, myTaskCheckListTasksListName));

  const updateTaskResultStatus = useAppSelector((state) =>
    selectJustUpdatedItem(state, myTaskCheckListResultsListName),
  );

  useEffect(() => {
    if (!email || !jobTitleStr) {
      dispatch(resetChecklistTasks());
      return;
    }

    dispatch(
      fetchChecklistTasks({
        email,
        jobTitle: jobTitleStr,
        periodDate: periodCheckListDate,
        currentUserEmail: currentUser.email,
      }),
    );
  }, [
    dispatch,
    email,
    jobTitleStr,
    currentUser.email,
    JSON.stringify(periodCheckListDate),
    addTaskStatus,
    updateConnectionStatus,
    updateTaskResultStatus,
  ]);

  useEffect(() => {
    setShiftComment("");
  }, [email, shiftDate]);

  useEffect(() => {
    if (completedShift) {
      setShiftComment(completedShift.Comment || "");
    }
  }, [completedShift?.ID, completedShift?.Comment]);

  // useEffect(() => {
  //   if (updateTaskStatus) {
  //     const newItemsArray = items.filter((item) => item.ID !== updateTaskStatus);
  //     setItems(newItemsArray);
  //   }
  // }, [updateTaskStatus]);

  useEffect(() => {
    dispatch(getItems({ listName: myTaskCheckListDirectionsListName }));
  }, []);

  useEffect(() => {
    if (taskCardId) {
      dispatch(setChecklistSelectedTaskId(taskCardId));
    }
  }, [dispatch, taskCardId]);

  const handleOpenTask = useCallback(
    (connectionId: number) => {
      const params = new URLSearchParams(searchParams);
      params.set("taskCard", String(connectionId));
      setSearchParams(params, { replace: true });
      dispatch(setChecklistSelectedTaskId(connectionId));
    },
    [dispatch, searchParams, setSearchParams],
  );

  const handleCloseTaskCard = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("taskCard");
    setSearchParams(params, { replace: true });
    dispatch(setChecklistSelectedTaskId(null));
  }, [dispatch, searchParams, setSearchParams]);

  const handleUpdateTask = useCallback(
    (connectionId: number, changes: Partial<TaskItem>) => {
      dispatch(updateChecklistTaskLocally({ connectionId, changes }));
    },
    [dispatch],
  );

  const handleReturnShiftToWork = useCallback(() => {
    if (!completedShift) {
      return;
    }

    dispatch(returnChecklistShiftToWork({ ID: completedShift.ID }));
  }, [dispatch, completedShift]);

  const handleChangeTask = useCallback(
    (key: keyof TaskItem, value: any) => {
      if (!taskCardId) return;
      dispatch(
        updateChecklistTaskLocally({ connectionId: taskCardId, changes: { [key]: value } as Partial<TaskItem> }),
      );
    },
    [dispatch, taskCardId],
  );

  const fetchChecklistExportRelatedData = useCallback(
    async (exportTasks: TaskItem[]) => {
      const tasksWithConnection = exportTasks.filter((task) => Boolean(task.ConnectionID));
      const taskIds = Array.from(new Set(tasksWithConnection.map((task) => Number(task.ConnectionID)).filter(Boolean)));
      const selectedDay =
        shiftDate || (periodCheckListDate?.startDate ? moment(periodCheckListDate.startDate).format("YYYY-MM-DD") : "");
      const authorEmail = currentUser.email.toLowerCase().trim();

      const [actions, commentsResponse, shiftResponse] = await Promise.all([
        fetchActionsByTasks(tasksWithConnection, authorEmail, periodCheckListDate?.startDate),
        taskIds.length
          ? getItemsRequest({
              listName: myTaskCheckListCommentsListName,
              filters: { Task: taskIds },
              populateFields: ["Task", "Author"],
            })
          : Promise.resolve({ items: [] }),
        email && selectedDay
          ? getItemsRequest({
              listName: myTaskShiftListName,
              filters: {
                ResponsibleEmail: email,
                Date: {
                  start: selectedDay,
                  end: selectedDay,
                },
                Finished: true,
              },
            })
          : Promise.resolve({ items: [] }),
      ]);

      const freshCommentsByTaskId = buildCommentsByTaskId(
        commentsResponse.items,
        tasksWithConnection,
        authorEmail,
        periodCheckListDate?.startDate,
      );
      const checklistComment = String(shiftResponse.items.find((shift: any) => shift.Finished)?.Comment || "");

      return {
        actions,
        comments: freshCommentsByTaskId,
        checklistComment,
      };
    },
    [email, currentUser.email, periodCheckListDate?.startDate, shiftDate],
  );

  const getModifiedTasksToExcel = useCallback(
    (
      tasks: TaskItem[],
      exportActionsByConnectionId: Record<string, any[]> = actionsByConnectionId,
      exportCommentsByTaskId: Record<string, string[]> = commentsByTaskId,
      checklistComment: string = "",
    ) => {
      const data: Array<Record<ColumnKey, any>> = [];
      const highlightPinkRowIndexes: number[] = [];

      tasks.forEach((task) => {
        const periodString = getPeriodDateString(task, periodCheckListDate?.startDate);
        const actionIds = (exportActionsByConnectionId[String(task.ConnectionID)] || []).map((action) =>
          String(action.ID),
        );
        const comments = exportCommentsByTaskId[String(task.ConnectionID)] || [];
        const rowCount = Math.max(actionIds.length, comments.length, 1);
        const highlightPink = task.Result.hasDeviation;
        const startIndex = data.length;

        const taskRow: Record<ColumnKey, any> = {
          ActionLinks: actionIds[0] || "",
          Comments: comments[0] || "",
          CompletionCriteria: task.Task.CompletionCriteria,
          Direction: task.Direction,
          hasDeviation:
            task.Result.hasDeviation && task.Result.isDone
              ? "Оба"
              : task.Result.hasDeviation
                ? "Выявлено отклонение"
                : "",
          isDone: task.Result.isDone && task.Result.hasDeviation ? "Оба" : task.Result.isDone ? "Выполнено" : "",
          Period: periodString,
          Title: task.Task.Title,
        };

        data.push(taskRow);

        if (rowCount > 1) {
          for (let index = 0; index < rowCount - 1; index++) {
            const rowIndex = index + 1;
            data.push({
              ActionLinks: actionIds[rowIndex] || "",
              Comments: comments[rowIndex] || "",
            } as Record<ColumnKey, any>);
          }
        }

        if (highlightPink) {
          for (let i = 0; i < rowCount; i++) {
            highlightPinkRowIndexes.push(startIndex + i);
          }
        }
      });

      if (checklistComment.trim()) {
        data.push({ Comments: checklistComment } as Record<ColumnKey, any>);
      }

      return {
        columns: exportTempData.columns,
        data,
        highlightPinkRowIndexes,
      };
    },
    [tasks, periodCheckListDate?.startDate, actionsByConnectionId, commentsByTaskId],
  ) as (
    tasks: TaskItem[],
    exportActionsByConnectionId?: Record<string, any[]>,
    exportCommentsByTaskId?: Record<string, string[]>,
    checklistComment?: string,
  ) => ExportDataType;

  const getActualExportData = useCallback(async () => {
    const freshData = await fetchChecklistExportRelatedData(tasks);
    setActionsByConnectionId(freshData.actions);
    setCommentsByTaskId(freshData.comments);
    return getModifiedTasksToExcel(tasks, freshData.actions, freshData.comments, freshData.checklistComment);
  }, [fetchChecklistExportRelatedData, getModifiedTasksToExcel, tasks]);

  const handleCompleteShift = useCallback(async () => {
    if (!email || !shiftDate || isShiftActionLoading) {
      return;
    }

    setIsCompletingShift(true);

    const emailBody = buildChecklistShiftCompletionText({
      tasks,
      comment: shiftComment,
      date: shiftDate,
      employeeName: users[email]?.displayName || currentUser.displayName || email,
      managerName: getChecklistShiftManagerName(email, users),
    });
    const managerEmail = getChecklistShiftManagerEmail(email, users);

    if (managerEmail) {
      try {
        const fileName = `Задачи за ${moment(periodCheckListDate?.startDate).format("DD.MM.YYYY")}`;
        const exportData = await getActualExportData();
        const excelBlob = await createExcelBlob(exportData, fileName, "checklist_tasks");
        const file = excelBlob ? await uploadChecklistShiftExcelFile(excelBlob, `${fileName}.xlsx`, true) : null;

        await emailAPI().sendEmail({
          Title: "Мои задачи | Чек-лист подчиненного",
          Subject: "Мои задачи | Чек-лист подчиненного",
          Body: emailBody,
          Recepients: managerEmail,
          DateToBeSentAt: moment().subtract(12, "hours").toDate(),
          AttachmentPaths: file ? [file.url] : [],
        });
      } catch (error) {
        console.error("Не удалось создать письмо о завершении смены", error);
      }
    } else {
      console.error("Не удалось определить email руководителя для письма о завершении смены", { email });
    }

    dispatch(
      completeChecklistShift({
        email,
        date: shiftDate as any,
        comment: shiftComment,
      }),
    );
    setIsCompletingShift(false);
  }, [
    dispatch,
    email,
    shiftDate,
    isShiftActionLoading,
    shiftComment,
    tasks,
    users,
    currentUser.displayName,
    periodCheckListDate?.startDate,
    getActualExportData,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetChecklistTasks());
      dispatch(resetChecklistPeriodDate());
    };
  }, [dispatch, page]);

  return (
    <section className="mb-7.5 cursor-pointer w-full flex-col flex z-10 shadow-transparent">
      <TGDisclosure
        defaultOpen={true}
        title={<h3 className="font-medium text-primaryGray text-[14px]">Чек-лист</h3>}
        wrapperClassName="border-none pt-[16px] tablet:pt-3.5"
        panelClassName={"p-0"}
        buttonClassName={"p-0"}
        iconArrowClassName="w-[24px] tablet:w-7 h-[24px] tablet:h-7 text-gray-400 hover:text-darkblue"
      >
        <div className="grid items-center gap-[20px] grid-cols-[1fr] tablet:grid-cols-[1fr_176px_auto] mt-4">
          <Datepicker
            containerClassName={"z-20 relative"}
            value={periodCheckListDate}
            onChange={(dateObjWithString) => {
              const startParsedDate = dateObjWithString?.startDate
                ? new Date(dateObjWithString?.startDate)
                : new Date();
              const endParsedDate = dateObjWithString?.endDate ? new Date(dateObjWithString?.endDate) : new Date();

              dispatch(
                setChecklistPeriodDate({
                  startDate: startParsedDate,
                  endDate: endParsedDate,
                }),
              );
            }}
            asSingle={true}
            useRange={false}
            dateLooking="middle"
            displayFormat="DD.MM.YYYY"
            startWeekOn="mon"
            placeholder="Укажите даты"
            i18n="ru"
            inputClassName={(p) => p + " font-medium"}
            primaryColor="blue"
            minDate={minDate}
            maxDate={getChecklistShiftDayDate()}
            readOnly={true}
          />
          {page === "main" && (
            <TGButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isShiftCompleted) {
                  setOpenReturnShiftModal(true);
                } else {
                  handleCompleteShift();
                }
              }}
              className="h-[50px] text-base text-darkblue"
              disabled={isShiftActionLoading}
              loading={isShiftActionLoading}
            >
              {isShiftCompleted ? "Вернуть в работу" : "Завершить смену"}
            </TGButton>
          )}
        </div>
        {page === "main" && (
          <MyTaskCheckListTable
            periodDate={periodCheckListDate}
            items={tasks}
            loading={loadingItems}
            cb={() => {}}
            onOpenTask={handleOpenTask}
            onUpdateTask={handleUpdateTask}
          />
        )}
        {page === "myTeam" && (
          <MyTaskCheckListMyTeamTable
            periodDate={periodCheckListDate}
            items={tasks}
            loading={loadingItems}
            cb={() => {}}
            onOpenTask={handleOpenTask}
          />
        )}
        {isShiftCompleted && (
          <div className="flex flex-col gap-y-4 mt-4">
            <h4 className="font-normal text-primaryGray">Комментарий</h4>
            <textarea
              value={completedShift?.Comment || ""}
              placeholder="Введите ваш комментарий"
              disabled={true}
              className="block min-h-[68px] w-full resize-none rounded-md border-0 bg-[#E5E7EB] px-5 py-4 text-base text-[#374151] placeholder-[#374151] opacity-100"
            />
            {page === "main" && (
              <div className="grid grid-cols-2 gap-x-[15px]">
                <AddOptionalTaskButton email={email} jobTitleStr={jobTitleStr} disabled={isShiftCompleted} />
                <TGButton
                  type="button"
                  onClick={() => setOpenReturnShiftModal(true)}
                  className="h-[50px] w-full text-base text-darkblue"
                  disabled={isShiftActionLoading}
                  loading={isShiftActionLoading}
                >
                  Вернуть в работу
                </TGButton>
              </div>
            )}
          </div>
        )}
        {page === "myTeam" && (
          <AddOptionalTaskButton
            email={email}
            jobTitleStr={jobTitleStr}
            disabled={isShiftCompleted}
            className="w-full"
          />
        )}
        {page === "main" && !isShiftCompleted && (
          <div className="flex flex-col gap-y-4">
            <textarea
              value={shiftComment}
              onChange={(e) => setShiftComment(e.target.value)}
              placeholder="Введите ваш комментарий"
              className="block min-h-[72px] w-full resize-none rounded-md border border-gray-300 bg-white px-5 py-3 text-base text-black placeholder-[#9CA3AF] focus:border-transparent"
            />
            <div className="grid grid-cols-2 gap-x-[15px]">
              <AddOptionalTaskButton email={email} jobTitleStr={jobTitleStr} />
              <TGButton
                type="button"
                onClick={handleCompleteShift}
                className="h-[50px] w-full text-base text-darkblue"
                disabled={isShiftActionLoading}
                loading={isShiftActionLoading}
              >
                Завершить смену
              </TGButton>
            </div>
          </div>
        )}
      </TGDisclosure>
      <Modal
        zIndex={1000}
        open={openReturnShiftModal}
        toggleDialog={setOpenReturnShiftModal}
        hideXMarkIcon={true}
        modalClassName="min-h-px min-w-[600px] max-w-[600px] py-0"
      >
        <header className="border-b border-[#E5E7EB] px-8 pt-9 pb-4">
          <h2 className="text-2xl font-semibold text-black">Вы уверены, что хотите вернуть в работу?</h2>
        </header>
        <div className="flex justify-between items-center px-8 py-8">
          <TGButton type="button" onClick={() => setOpenReturnShiftModal(false)}>
            Отмена
          </TGButton>
          <TGButton
            type="button"
            color="bluedark"
            onClick={() => {
              setOpenReturnShiftModal(false);
              handleReturnShiftToWork();
            }}
          >
            Вернуть
          </TGButton>
        </div>
      </Modal>
    </section>
  );
}

export default CheckListsMain;

type TaskType = {
  ID: number;
  Title: string;
  Description: string;
  CompletionCriteria: string;
  Direction: LookUpRecord;
  Test: string;
  ResponsibleEmail: string;
};

export type TaskFormValues = {
  task: string;
  description: string;
  completionCriteria: string;
  direction: Option[];
  frequency: Option[];
  additionalFields: { id: string; type: string; label: string; answer: string }[];
};

type OpenFormModalType = "create" | "open" | "close";

function AddOptionalTaskButton({
  email,
  jobTitleStr,
  disabled = false,
  className = "",
}: {
  email: string;
  jobTitleStr: string;
  disabled?: boolean;
  className?: string;
}) {
  const JustAddedItemID = useAppSelector((state) => selectJustAddedItem(state, myTaskCheckListTasksListName));

  const [openModal, setOpenModal] = useState(false);
  const [openFormModal, setOpenFormModal] = useState<OpenFormModalType>("close");
  const [tasks, setTasks] = useState<TaskType[]>([]);
  const [selectedTask, setSelectedTask] = useState<Option[]>([]);
  const [openCloseDialog, setOpenCloseDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState<TaskFormValues>({
    task: "",
    description: "",
    completionCriteria: "",
    direction: [],
    frequency: [],
    additionalFields: [],
  });

  function toggleModal(value: boolean) {
    setOpenModal(value);
    if (!value) {
      setSelectedTask([]);
    }
  }

  useEffect(() => {
    if (openFormModal !== "close") {
      setOpenModal(false);
    }
  }, [openFormModal]);

  function toggleFormModal(value: OpenFormModalType) {
    setOpenFormModal(value);
    if (value === "close") {
      setOpenModal(false);
    }
  }

  async function changeTaskByID(id: string) {
    const task = await getTaskByID(id);

    setDefaultValues({
      task: id,
      description: task.Description,
      completionCriteria: task.CompletionCriteria,
      direction: [{ id: task.Direction.ID.toString(), name: task.Direction.Title, value: task.Direction }],
      frequency: [{ id: "1", name: "По необходимости", value: { ID: 1, Title: "По необходимости" } }],
      additionalFields: task.Test ? JSON.parse(task.Test) : [],
    });
  }

  async function changeDefaultValues(type: OpenFormModalType) {
    if (type === "open") {
      changeTaskByID(selectedTask[0].id);
    } else {
      setDefaultValues({
        task: "",
        description: "",
        completionCriteria: "",
        direction: [],
        frequency: [],
        additionalFields: [],
      });
    }

    toggleFormModal(type);
  }

  function toggleCloseDialog(value: boolean) {
    setOpenCloseDialog(value);
  }

  useAsyncEffect(async () => {
    try {
      const checkListResponse = await getItemsRequest({
        listName: myTaskCheckListsListName,
        filters: { UserPosition: jobTitleStr + "*" },
      });

      if (checkListResponse.items.length === 0) {
        return;
      }

      const checkListIDs = checkListResponse.items.map((item) => item.ID);

      if (checkListIDs.length > 0) {
        const connectionResponse = await getItemsRequest({
          listName: myTaskCheckListConnectionWithTasksListName,
          filters: { CheckList: checkListIDs },
        });

        const connections = modifiedItemsState(connectionResponse.items, myTaskCheckListConnectionWithTasksListName);

        if (connections.length > 0) {
          const tasks: TaskType[] = connections.reduce((acc: TaskType[], connection) => {
            const isHasAdded = acc.some((t) => t.ID === connection.Task.ID);

            if (isHasAdded) return acc;

            if (
              connection.ResponsibleEmail !== "all" &&
              connection.ResponsibleEmail !== email &&
              connection.Title !== "Задача удалена"
            ) {
              return acc;
            }

            const task: TaskType = {
              ID: connection.Task.ID,
              Title: connection.Task.Title,
              Test: connection.Task.Metadata?.Test || "",
              Direction: connection.Task.Metadata?.Direction?.Title || "",
              Description: connection.Task.Metadata?.Description || "",
              CompletionCriteria: connection.Task.Metadata?.CompletionCriteria || "",
              ResponsibleEmail: connection.ResponsibleEmail || "",
            };

            acc.push(task);

            return acc;
          }, []);

          setTasks(tasks);
        }
      }
    } catch (error) {
      console.error(error, "AddOptionalTaskButton");
    }
  }, [email, JustAddedItemID]);

  return (
    <>
      <TGButton
        className={twMerge("h-[50px] text-primaryGray", className)}
        icon={<PlusCircleIcon width={20} height={20} />}
        iconStyle="mt-[1px]"
        disabled={disabled}
        onClick={() => toggleModal(true)}
      >
        Добавить опциональную задачу
      </TGButton>
      <Modal
        zIndex={1000}
        open={openModal}
        toggleDialog={(t) => toggleModal(t)}
        modalClassName="min-h-0 max-w-[600px] p-10"
      >
        <header className="pb-10">
          <h2>Добавить опциональную задачу</h2>
        </header>

        <div className="grow custom-selects">
          <TGSelectPickerByList
            list={tasks.map<Option>((t) => ({
              id: t.ID.toString(),
              name: t.Title,
              value: t,
            }))}
            values={selectedTask}
            onChange={(values) => setSelectedTask(values)}
            fetchKey="Task"
            classNames={{
              selectClassName: "!px-5",
              labelClassName: "text-base",
            }}
            searchPlaceholder="Найти задачу"
            dataField={{
              hideRemoveButton: true,
              placeholder: "Выберите задачу",
              type: "peoplePicker",
              require: true,
              title: "Выберите задачу",
              width: "w-full",
              key: "peoplePicker",
              single: true,
            }}
            errors={{}}
          />
        </div>
        <div className="pb-5 space-y-5">
          <TGButton
            icon={<EyeIcon />}
            disabled={selectedTask.length === 0}
            type="button"
            color="bluedark"
            onClick={() => changeDefaultValues("open")}
            className="w-full"
          >
            Открыть выбранную задачу
          </TGButton>
          <TGButton
            icon={<CirclePlus />}
            type="button"
            onClick={() => changeDefaultValues("create")}
            className="w-full"
          >
            Создать новую задачу
          </TGButton>
        </div>
      </Modal>

      {openFormModal === "create" && (
        <DrawerComponent percentage="87" zIndex={1001} open={true} toggleDrawer={(t) => toggleFormModal("close")}>
          <TaskFormCreate
            email={email}
            defaultValues={defaultValues}
            toggleFormModal={() => {
              toggleFormModal("close");
              toggleModal(false);
            }}
            toggleCloseDialog={(state) => {
              toggleCloseDialog(state);
            }}
          />
        </DrawerComponent>
      )}
      {openFormModal === "open" && selectedTask.length > 0 && (
        <DrawerComponent percentage="87" zIndex={1001} open={true} toggleDrawer={(t) => toggleFormModal("close")}>
          <TaskFormOpen
            email={email}
            tasks={tasks}
            defaultValues={defaultValues}
            selectedTask={selectedTask}
            setSelectedTask={setSelectedTask}
            toggleFormModal={() => {
              toggleFormModal("close");
              toggleModal(false);
            }}
          />
        </DrawerComponent>
      )}

      <Modal zIndex={1002} open={openCloseDialog} toggleDialog={toggleCloseDialog} modalClassName="min-h-px">
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <div>
            Вы уверены, что хотите закрыть окно? <br />
            Вся введенная вами информация будет сброшена.
          </div>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton onClick={() => setOpenCloseDialog(false)}>Отмена</TGButton>
          <TGButton
            color="bluedark"
            onClick={() => {
              setOpenCloseDialog(false);
              toggleFormModal("close");
              toggleModal(false);
            }}
          >
            Удалить
          </TGButton>
        </footer>
      </Modal>
    </>
  );
}

function TaskFormCreate({
  defaultValues,
  toggleFormModal,
  toggleCloseDialog,
  email,
}: {
  defaultValues: TaskFormValues;
  toggleFormModal: (value: boolean) => void;
  toggleCloseDialog: (value: boolean) => void;
  email: string;
}) {
  const directionsItems = useAppSelector((state) => selectItems(state, myTaskCheckListDirectionsListName));
  const users: any = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();
  const form = useForm<TaskFormValues>({
    defaultValues: defaultValues,
  });

  const [customLoading, setCustomLoading] = useState(false);

  // Обновляем форму при изменении defaultValues
  useEffect(() => {
    form.reset(defaultValues);
  }, [JSON.stringify(defaultValues)]);

  const onSubmit = async (data: TaskFormValues) => {
    setCustomLoading(true);

    try {
      const checkListResponse = await getItemsRequest({
        listName: myTaskCheckListsListName,
        filters: { UserPosition: users[email].jobTitleStr + "*" },
      });
      const userCheckList = checkListResponse.items[0];

      let userCheckListID: number = 0;

      if (!userCheckList) {
        userCheckListID = (await addItemRequest({
          listName: myTaskCheckListsListName,
          body: {
            Title: `Чек-лист для пользователей # ${users[email].jobTitleStr}`,
            UserPosition: users[email].jobTitleStr,
            ResponsibleEmail: "all",
          },
        })) as number;
      } else {
        userCheckListID = userCheckList.ID;
      }

      if (!userCheckListID) {
        throw new Error("Не удалось получить ID чек-листа");
      }

      const userTaskID = await addItemRequest({
        listName: myTaskCheckListTasksListName,
        body: {
          Title: data.task,
          Description: data.description,
          CompletionCriteria: data.completionCriteria,
          Test: JSON.stringify(data.additionalFields),
          Direction: data.direction[0].id,
          ResponsibleEmail: email,
        },
      });

      const connectionID = await addItemRequest({
        listName: myTaskCheckListConnectionWithTasksListName,
        body: {
          Title: `Задача к чек-листу # ${userTaskID}`,
          Task: userTaskID,
          CheckList: userCheckListID,
          sort: "99999999",
          TaskPeriod: data.frequency[0].name,
          ResponsibleEmail: email,
          // EndDate: endDate,
        },
      });

      if (connectionID) {
        dispatch(addItemSuccess({ listName: myTaskCheckListTasksListName, ID: userTaskID }));
        console.log("Задача добавлена в чек-лист: ", connectionID);
      }
    } catch (error) {
      console.error(error, (error as Error).message || "Неизвестная ошибка");
    } finally {
      setCustomLoading(false);
      toggleFormModal(false);
      toggleCloseDialog(false);
    }
  };

  const directions = directionsItems.map<Option>((d) => ({
    id: d.ID.toString(),
    name: d.Title,
    value: d,
  }));

  return (
    <FlexCol tabIndex={0}>
      {(customLoading && (
        <FlexCol className="px-7.5 py-5 justify-center items-center gap-5 w-full h-full">
          <span className="text-base text-darkblue">Добавляем задачу...</span>
          <Loading />
        </FlexCol>
      )) || (
        <>
          <FlexRow className="flex px-10 pt-10 pb-8">
            <h1 className="text-black">Новая опциональная задача</h1>
          </FlexRow>
          <FormProvider {...form}>
            <form
              className="flex flex-row flex-wrap px-10 custom-selects"
              onSubmit={form.handleSubmit((data) => onSubmit(data))}
            >
              <FlexCol className="gap-y-5 w-full">
                <FlexRow>
                  <Controller
                    control={form.control}
                    name="task"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <SearchUI
                        label="Название"
                        id="task"
                        autoFocus={true}
                        {...field}
                        require={true}
                        inputClass="text-black"
                        labelClass="text-base font-normal text-black"
                        onClick={field.onChange}
                        defaultValue={field.value}
                        placeholder="Название"
                        search={false}
                      />
                    )}
                  />
                </FlexRow>
                <FlexRow>
                  <Controller
                    control={form.control}
                    name="description"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <SearchUI
                        label="Описание"
                        id="description"
                        {...field}
                        require={true}
                        textArea={true}
                        inputClass="text-black"
                        labelClass="text-base font-normal text-black"
                        onClick={field.onChange}
                        defaultValue={field.value}
                        placeholder="Описание"
                        search={false}
                      />
                    )}
                  />
                </FlexRow>
                <FlexRow>
                  <Controller
                    control={form.control}
                    name="completionCriteria"
                    render={({ field }) => (
                      <SearchUI
                        label="Критерий выполнения"
                        id="completionCriteria"
                        {...field}
                        textArea={true}
                        inputClass="text-black"
                        labelClass="text-base font-normal text-black"
                        onClick={field.onChange}
                        defaultValue={field.value}
                        placeholder="Критерий выполнения"
                        search={false}
                      />
                    )}
                  />
                </FlexRow>
                <FlexRow>
                  <Controller
                    rules={{ required: true }}
                    render={({
                      field: { onChange, onBlur, value, name, ref, disabled },
                      fieldState: { invalid, isTouched, isDirty, error },
                      formState,
                    }) => (
                      <TGSelectPickerByList
                        values={value}
                        onChange={onChange}
                        fetchKey="Direction"
                        classNames={{
                          selectClassName: "!px-5",
                          labelClassName: "text-base font-normal",
                        }}
                        searchPlaceholder="Найти направление"
                        dataField={{
                          placeholder: "Выберите направление",
                          type: "peoplePicker",
                          require: true,
                          title: "Направление",
                          width: "w-full",
                          disabled: disabled,
                          key: "direction",
                          single: true,
                          hideRemoveButton: true,
                        }}
                        list={directions}
                        errors={{ direction: !!error }}
                      />
                    )}
                    name="direction"
                    control={form.control}
                  />
                </FlexRow>
                <FlexRow>
                  <Controller
                    rules={{ required: true }}
                    render={({
                      field: { onChange, onBlur, value, name, ref, disabled },
                      fieldState: { invalid, isTouched, isDirty, error },
                      formState,
                    }) => (
                      <TGSelectPickerByList
                        values={value}
                        onChange={onChange}
                        fetchKey="Frequencies"
                        classNames={{
                          selectClassName: "!px-5",
                          labelClassName: "text-base font-normal",
                        }}
                        searchPlaceholder="Найти периодичность"
                        dataField={{
                          placeholder: "Выберите периодичность",
                          type: "peoplePicker",
                          require: true,
                          title: "Периодичность",
                          width: "w-full",
                          disabled: disabled,
                          key: "frequency",
                          single: true,
                          hideRemoveButton: true,
                        }}
                        list={frequencyCheckList}
                        errors={{ frequency: !!error }}
                      />
                    )}
                    name="frequency"
                    control={form.control}
                  />
                </FlexRow>
              </FlexCol>

              <FlexCol className="w-full">
                <h4 className="my-5 font-normal text-primaryGray">Опросник</h4>
                <AdditionalFieldsProvider>
                  <CheckListAdditionalMeetingFields />
                </AdditionalFieldsProvider>
              </FlexCol>

              <footer className="flex justify-between py-10 w-full shrink-0">
                <TGButton
                  type="button"
                  onClick={() => (form.formState.isDirty ? toggleFormModal(false) : toggleCloseDialog(true))}
                >
                  Назад
                </TGButton>
                <TGButton type="submit" color="bluedark">
                  Добавить задачу в чек-лист
                </TGButton>
              </footer>
            </form>
          </FormProvider>
        </>
      )}
    </FlexCol>
  );
}

function getPeriodDateString(task: TaskItem, date: DateType | undefined) {
  const { start, end } = getStartEndDateByPeriod(task.Period, date);

  const periodString = `${task.Period} · ${moment(start).format("DD.MM.YYYY")} ${
    !moment(start).isSame(end)
      ? task.Period === "Неделя" || task.Period === "Месяц" || task.Period === "Квартал" || task.Period === "Год"
        ? "\u2014 " + moment(end).subtract(1, "day").format("DD.MM.YYYY")
        : "\u2014 " + moment(end).format("DD.MM.YYYY")
      : ""
  }`;

  return periodString;
}

function getChecklistShiftManagerName(email: string, users: Record<string, any>) {
  const managerEmail = getChecklistShiftManagerEmail(email, users);

  if (!managerEmail) {
    return "руководитель";
  }

  return users[managerEmail]?.displayName || managerEmail;
}

function getChecklistShiftManagerEmail(email: string, users: Record<string, any>) {
  return users[email]?.manager || "";
}

async function uploadChecklistShiftExcelFile(blob: Blob, fileName: string, fullResponse: boolean = false) {
  const file = new File([blob], fileName, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const form = new FormData();

  form.append("file", file);
  form.append("folderId", "27");

  const response = await fetch(`${strapiUrl}media/upload`, {
    method: "POST",
    credentials: "include",
    body: form,
  });

  if (!response.ok) {
    throw new Error(`Не удалось загрузить Excel файл: ${response.status} ${response.statusText}`);
  }

  const uploadResult = await response.json();
  const uploadedFile = uploadResult?.data?.[0];

  if (!uploadedFile?.id) {
    throw new Error("Не удалось получить ID загруженного Excel файла");
  }

  if (fullResponse) {
    return uploadedFile;
  }

  return Number(uploadedFile.id);
}

function buildChecklistShiftCompletionText({
  tasks,
  comment,
  date,
  employeeName,
  managerName,
}: {
  tasks: TaskItem[];
  comment: string;
  date: string;
  employeeName: string;
  managerName: string;
}) {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((task) => task.Result.isDone).length;
  const completedPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const formattedDate = moment(date).format("DD.MM.YYYY");
  const commentText = comment.trim() || "-";

  return [
    `Здравствуйте, ${managerName},`,
    "",
    `Сотрудник ${employeeName} завершил выполнение задач за ${formattedDate}`,
    "",
    `Задач выполнено ${completedTasks} из ${totalTasks}, процент выполнения ${completedPercent}%`,
    "",
    "Детальная информация доступна во вложении",
    "",
    "Комментарии:",
    commentText,
  ]
    .join("\n")
    .replace(/\r\n|\r|\n/g, "<br/>");
}

export function TaskFormOpen({
  tasks,
  defaultValues,
  selectedTask,
  setSelectedTask,
  toggleFormModal,
  email,
}: {
  tasks: TaskType[];
  defaultValues: TaskFormValues;
  selectedTask: Option[];
  setSelectedTask: (value: Option[]) => void;
  toggleFormModal: (value: boolean) => void;
  email: string;
}) {
  const users: any = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();
  const form = useForm<TaskFormValues>({
    defaultValues: defaultValues,
  });

  const [customLoading, setCustomLoading] = useState(false);

  const [taskTitle, setTaskTitle] = useState("");

  // Обновляем форму при изменении defaultValues
  useEffect(() => {
    form.reset(defaultValues);
  }, [JSON.stringify(defaultValues)]);

  const onSubmit = async (data: TaskFormValues) => {
    setCustomLoading(true);

    try {
      const checkListResponse = await getItemsRequest({
        listName: myTaskCheckListsListName,
        filters: { UserPosition: users[email].jobTitleStr + "*" },
      });
      const userCheckList = checkListResponse.items[0];

      let userCheckListID: number = 0;

      if (!userCheckList) {
        userCheckListID = (await addItemRequest({
          listName: myTaskCheckListsListName,
          body: {
            Title: `Чек-лист для пользователя # ${email}`,
            UserPosition: users[email].jobTitleStr,
            ResponsibleEmail: "all",
          },
        })) as number;
      } else {
        userCheckListID = userCheckList.ID;
      }

      if (!userCheckListID) {
        throw new Error("Не удалось получить ID чек-листа");
      }

      const userTaskID = await addItemRequest({
        listName: myTaskCheckListTasksListName,
        body: {
          Title: data.task,
          Description: data.description,
          CompletionCriteria: data.completionCriteria,
          Test: JSON.stringify(data.additionalFields),
          Direction: data.direction[0].id,
          ResponsibleEmail: email,
        },
      });

      const connectionID = await addItemRequest({
        listName: myTaskCheckListConnectionWithTasksListName,
        body: {
          Title: `Задача к чек-листу # ${userTaskID}`,
          Task: userTaskID,
          CheckList: userCheckListID,
          sort: "99999999",
          TaskPeriod: data.frequency[0].name,
          ResponsibleEmail: email,
        },
      });

      if (connectionID) {
        dispatch(addItemSuccess({ listName: myTaskCheckListTasksListName, ID: userTaskID }));
        console.log("Задача добавлена в чек-лист: ", connectionID);
      }
    } catch (error) {
      console.error(error, (error as Error).message || "Неизвестная ошибка");
    } finally {
      setCustomLoading(false);
      toggleFormModal(false);
    }
  };

  useEffect(() => {
    async function fetchTask() {
      if (selectedTask.length > 0) {
        const task = await getTaskByID(selectedTask[0].id);
        setTaskTitle(task.Title);

        const connectionResponse = await getItemsRequest({
          listName: myTaskCheckListConnectionWithTasksListName,
          filters: { Task: selectedTask[0].id },
        });

        const connection = connectionResponse.items[0];

        if (task) {
          form.setValue("description", task.Description);
          form.setValue("completionCriteria", task.CompletionCriteria);
          form.setValue("direction", [
            { id: task.Direction.ID.toString(), name: task.Direction.Title, value: task.Direction },
          ]);
          form.setValue("task", task.Title);
        }

        form.setValue("frequency", [
          { id: connection.TaskPeriod, name: connection.TaskPeriod, value: connection.TaskPeriod },
        ]);
      }
    }
    fetchTask();
  }, [selectedTask[0]?.id]);

  return (
    <FlexCol>
      {(customLoading && (
        <FlexCol className="px-7.5 py-5 justify-center items-center gap-5 w-full h-full">
          <span className="text-base text-darkblue">Добавляем задачу...</span>
          <Loading />
        </FlexCol>
      )) || (
        <>
          <FlexRow className="flex px-10 pt-10 pb-8">
            <h1 className="text-black">{taskTitle ?? "Опциональная задача"}</h1>
          </FlexRow>
          <FormProvider {...form}>
            <form
              className="flex flex-row flex-wrap px-10 custom-selects"
              onSubmit={form.handleSubmit((data) => onSubmit(data))}
            >
              <FlexCol className="gap-y-5 w-full">
                <FlexRow>
                  <Controller
                    control={form.control}
                    name="task"
                    rules={{ required: true }}
                    render={({ field, fieldState: { error } }) => (
                      <TGSelectPickerByList
                        values={selectedTask}
                        onChange={(values) => {
                          field.onChange(values[0]["name"]);
                          setSelectedTask(values);
                        }}
                        fetchKey="Task"
                        classNames={{
                          selectClassName: "!px-5",
                          labelClassName: "text-base font-normal",
                        }}
                        searchPlaceholder="Найти задачу"
                        dataField={{
                          placeholder: "Выберите задачу",
                          type: "peoplePicker",
                          require: true,
                          title: "Выберите задачу",
                          width: "w-full mb-0",
                          disabled: false,
                          key: "task",
                          single: true,
                          hideRemoveButton: true,
                        }}
                        list={tasks.map<Option>((t) => ({
                          id: t.ID.toString(),
                          name: t.Title,
                          value: t,
                        }))}
                        errors={{ task: !!error }}
                      />
                    )}
                  />
                </FlexRow>
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Направление</h4>
                    <p className="text-base font-medium text-black">{form.watch("direction")[0]?.name ?? ""}</p>
                  </div>
                </FlexRow>
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Описание</h4>
                    <p className="text-base font-medium text-black">{form.watch("description") ?? ""}</p>
                  </div>
                </FlexRow>
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Критерий выполнения</h4>
                    <p className="text-base font-medium text-black whitespace-pre-line">
                      {form.watch("completionCriteria")}
                    </p>
                  </div>
                </FlexRow>
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Должность</h4>
                    <p className="text-base font-medium text-black">{users[email].jobTitleStr ?? ""}</p>
                  </div>
                </FlexRow>
                <FlexRow>
                  <Controller
                    rules={{ required: true }}
                    render={({
                      field: { onChange, onBlur, value, name, ref, disabled },
                      fieldState: { invalid, isTouched, isDirty, error },
                      formState,
                    }) => (
                      <TGSelectPickerByList
                        values={value}
                        onChange={onChange}
                        fetchKey="Frequencies"
                        classNames={{
                          selectClassName: "!px-5",
                          labelClassName: "text-base font-normal",
                        }}
                        searchPlaceholder="Найти периодичность"
                        dataField={{
                          placeholder: "Выберите периодичность",
                          type: "peoplePicker",
                          require: true,
                          title: "Периодичность",
                          width: "w-full",
                          disabled: disabled,
                          key: "frequency",
                          single: true,
                          hideRemoveButton: true,
                        }}
                        list={frequencyCheckList}
                        errors={{ frequency: !!error }}
                      />
                    )}
                    name="frequency"
                    control={form.control}
                  />
                </FlexRow>
              </FlexCol>

              <FlexCol className="w-full">
                <h4 className="my-5 font-normal text-primaryGray">Опросник</h4>
                <AdditionalFieldsProvider>
                  <CheckListAdditionalMeetingFields />
                </AdditionalFieldsProvider>
              </FlexCol>
              <footer className="flex justify-between py-10 w-full shrink-0">
                <TGButton type="button" onClick={() => toggleFormModal(false)}>
                  Назад
                </TGButton>
                <TGButton type="submit" color="bluedark">
                  Добавить задачу в чек-лист
                </TGButton>
              </footer>
            </form>
          </FormProvider>
        </>
      )}
    </FlexCol>
  );
}
